import React, { Dispatch, FC, Ref, SetStateAction, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Box from '@material-ui/core/Box';
import makeStyles from '@material-ui/core/styles/makeStyles';
import {
  Checkbox,
  Container,
  createStyles,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  Typography,
  withStyles,
} from '@material-ui/core';
import orderBy from 'lodash/orderBy';
import isFunction from 'lodash/isFunction';
import clsx from 'clsx';
import { format } from 'date-fns';
import MeasurementInput, { MeasurementInputControl } from './MeasurmentInput';
import ChartButtons from './ChartButtons';
import useDebounced from '../../../../util/useDebounced';

export interface MeasurementsV4 {
  key: string;
  timestamp?: number;
  timestampString?: string;
  painScore?: number;
  ice?: boolean;
  reposition?: boolean;
  painMedicationGiven?: boolean;
  reassessment?: boolean;
  reassessmentValue?: string;
  other?: boolean;
  otherValue?: string;
  rnInitials?: string;
}

export type Value = {
  measurmentsPain: MeasurementsV4[];
  signature?: {
    signature: string;
    signedBy: string;
  };
};

interface ChartProps {
  value: Value;
  onChange: (val: Value) => void;
}

const StyledTable = withStyles((theme: Theme) =>
  createStyles({
    root: {
      borderBottom: `2px solid ${theme.palette.divider}`,
    },
  })
)(Table);

const StyledTableCell = withStyles((theme: Theme) =>
  createStyles({
    head: {
      borderRight: `2px solid ${theme.palette.divider}`,
      borderBottom: `2px solid ${theme.palette.divider}`,
      '&:last-of-type': {
        borderRight: 'none',
      },
    },
    body: {
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
      fontSize: 14,
      verticalAlign: 'top',
      borderRight: `2px solid ${theme.palette.divider}`,
      '&:last-of-type': {
        borderRight: 'none',
      },
    },
  })
)(TableCell);

const StyledTableRow = withStyles((theme: Theme) =>
  createStyles({
    root: {
      border: '2px solid transparent',
      '&:last-of-type': {
        borderBottom: `2px solid ${theme.palette.divider}`,
      },
    },
  })
)(TableRow);

const getSecondsSinceStartOfDay = (date: Date) => {
  const startOfDay = new Date(date);
  startOfDay.setHours(0, 0, 0, 0);
  return (date.getTime() - startOfDay.getTime()) / 1000;
};

const ChartV4: FC<ChartProps> = ({ value: { measurmentsPain: ms, signature }, onChange }) => {
  const classes = useStyles();
  const measurments = ms;
  const [selectedIndex, setSelectedIndex] = useState<number>(NaN);
  const debouncedSelectedIndex = useDebounced(selectedIndex);

  const ordered = useMemo(() => orderBy(measurments || [], ['timestamp']), [measurments]);

  useLayoutEffect(() => {
    if (selectedIndex >= ordered.length) {
      setSelectedIndex(NaN);
    }
  }, [ordered.length, selectedIndex]);

  const onAdd = () => {
    const now = new Date();
    const newMeasurement: MeasurementsV4 = {
      key: Math.random().toString(36).slice(2),
      timestamp: getSecondsSinceStartOfDay(now),
      timestampString: format(now, 'HH:mm'),
      painScore: undefined,
      ice: false,
      reposition: false,
      painMedicationGiven: false,
      reassessment: false,
      reassessmentValue: '',
      other: false,
      otherValue: '',
      rnInitials: '',
    };
    const newValue = orderBy([...measurments, newMeasurement], ['timestamp'], ['asc']);
    onChange({ measurmentsPain: newValue, signature });
    setSelectedIndex(newValue.findIndex(m => m.key === newMeasurement.key));
  };

  const onDelete = () => {
    if (!isFinite(debouncedSelectedIndex)) {
      return;
    }
    const newValue = [...ordered];
    newValue.splice(debouncedSelectedIndex, 1);
    onChange({ measurmentsPain: newValue, signature });
    setSelectedIndex(newValue.length > debouncedSelectedIndex ? debouncedSelectedIndex : newValue.length - 1);
  };

  const onUpdate = (index: number) => (valueOrUpdater: SetStateAction<MeasurementsV4>) => {
    const next = [...ordered];
    const prev = next[index];
    const nextValue = isFunction(valueOrUpdater) ? valueOrUpdater(prev) : valueOrUpdater;
    next.splice(index, 1, nextValue);
    onChange({ measurmentsPain: orderBy(next, ['timestamp'], ['asc']), signature });
  };

  return (
    <Box display="flex" flex={1} flexDirection="row">
      <Box className={classes.centered}>
        <Container maxWidth="md" className={classes.root}>
          <Box textAlign="center" mb={2}>
            <Typography variant="h3">Pain Assessment</Typography>
            <Typography variant="body2" color="textSecondary">
              (on arrival to PACU, q 30 min x 1 hour, q 1 hour and REASSESSMENT after pain medication given)
            </Typography>
          </Box>
          <TableContainer>
            <StyledTable>
              <TableHead>
                <TableRow>
                  <StyledTableCell width="15%">
                    <Typography variant="h4">Time</Typography>
                  </StyledTableCell>
                  <StyledTableCell width="15%">
                    <Typography variant="h4">Pain Score</Typography>
                  </StyledTableCell>
                  <StyledTableCell width="55%">
                    <Typography variant="h4">Intervention</Typography>
                  </StyledTableCell>
                  <StyledTableCell width="15%">
                    <Typography variant="h4">RN Initials</Typography>
                  </StyledTableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {ordered.map((measurment, i) => (
                  <Aspect
                    key={measurment.key}
                    measurment={measurment}
                    onChange={onUpdate(i)}
                    selected={selectedIndex === i}
                    onSelect={() => setSelectedIndex(i)}
                  />
                ))}
              </TableBody>
            </StyledTable>
          </TableContainer>
        </Container>
      </Box>
      <ChartButtons onAdd={onAdd} onDelete={onDelete} />
    </Box>
  );
};

const Aspect: FC<{
  measurment: MeasurementsV4;
  onChange: Dispatch<SetStateAction<MeasurementsV4>>;
  selected: boolean;
  onSelect: () => void;
}> = ({ measurment, onChange, selected, onSelect }) => {
  const classes = useStyles();
  const originalDate = useMemo(() => new Date(Number(measurment.timestamp)), [measurment.timestamp]);

  const timeRef = useRef<MeasurementInputControl>();
  const painRef = useRef<MeasurementInputControl>();
  const rnRef = useRef<MeasurementInputControl>();
  const keyboardNavigationRef = useRef(false);

  useEffect(() => {
    if (selected) {
      if (!keyboardNavigationRef.current) {
        if (!measurment.timestampString) timeRef.current?.focus();
        else if (measurment.painScore === undefined || isNaN(measurment.painScore)) painRef.current?.focus();
        else if (!measurment.rnInitials) rnRef.current?.focus();
      }
      keyboardNavigationRef.current = false;
    } else {
      timeRef.current?.blur();
      painRef.current?.blur();
      rnRef.current?.blur();
    }
  }, [selected]);

  const toggle = (field: keyof MeasurementsV4) => (_: unknown, checked: boolean) => {
    onChange(v => ({ ...v, [field]: checked }));
  };

  const setText = (field: keyof MeasurementsV4) => (value: string) => {
    onChange(v => ({ ...v, [field]: value }));
  };

  const handleFocus = () => {
    onSelect();
  };

  return (
    <StyledTableRow
      className={clsx({ [classes.selectedRow]: selected })}
      onClick={onSelect}
      style={{ cursor: 'pointer' }}
    >
      <StyledTableCell className={classes.value}>
        <Typography variant="h3" className={classes.value}>
          <MeasurementInput
            ref={timeRef as Ref<MeasurementInputControl>}
            format="MilitaryTime"
            preFill
            selectOnFocus
            value={measurment.timestampString ?? ''}
            onChange={timestamp => {
              onChange(v => ({
                ...v,
                ...(timestamp.length === 5
                  ? {
                      timestamp:
                        getSecondsSinceStartOfDay(
                          new Date(originalDate.toISOString().substring(0, 10) + 'T' + timestamp)
                        ) || 0,
                    }
                  : {}),
                timestampString: timestamp,
              }));
            }}
            onComplete={() => painRef.current?.focus()}
            onTab={() => {
              keyboardNavigationRef.current = true;
              painRef.current?.focus();
            }}
            onShiftTab={() => {
              keyboardNavigationRef.current = true;
              timeRef.current?.blur();
            }}
            onFocus={handleFocus}
          />
        </Typography>
      </StyledTableCell>

      <StyledTableCell className={classes.value}>
        <Typography variant="h3" className={classes.value}>
          <MeasurementInput
            ref={painRef as Ref<MeasurementInputControl>}
            format="PAIN"
            selectOnFocus
            value={measurment.painScore !== undefined && !isNaN(measurment.painScore) ? String(measurment.painScore) : ''}
            onChange={painScore => {
              onChange(v => ({ ...v, painScore: parseInt(painScore, 10) }));
            }}
            onComplete={() => rnRef.current?.focus()}
            onTab={() => {
              keyboardNavigationRef.current = true;
              rnRef.current?.focus();
            }}
            onShiftTab={() => {
              keyboardNavigationRef.current = true;
              timeRef.current?.focus();
            }}
            onFocus={handleFocus}
          />
        </Typography>
      </StyledTableCell>

      <StyledTableCell>
        <Box display="flex" flexDirection="column">
          <FormControlLabel
            control={<Checkbox color="primary" checked={!!measurment.ice} onChange={toggle('ice')} />}
            label="Ice"
          />
          <FormControlLabel
            control={<Checkbox color="primary" checked={!!measurment.reposition} onChange={toggle('reposition')} />}
            label="Reposition"
          />
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                checked={!!measurment.painMedicationGiven}
                onChange={toggle('painMedicationGiven')}
              />
            }
            label="Pain Medication given"
          />
          <Box display="flex" alignItems="center" pl={4} style={{ gap: 8 }}>
            <FormControlLabel
              control={
                <Checkbox color="primary" checked={!!measurment.reassessment} onChange={toggle('reassessment')} />
              }
              label="Reassessment after 15 minutes"
            />
            <input
              className={classes.inlineInput}
              value={measurment.reassessmentValue ?? ''}
              onChange={e => setText('reassessmentValue')(e.target.value)}
            />
          </Box>
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <FormControlLabel
              control={<Checkbox color="primary" checked={!!measurment.other} onChange={toggle('other')} />}
              label="Other"
            />
            <input
              className={classes.inlineInput}
              style={{ flex: 1 }}
              value={measurment.otherValue ?? ''}
              onChange={e => setText('otherValue')(e.target.value)}
            />
          </Box>
        </Box>
      </StyledTableCell>

      <StyledTableCell className={classes.value}>
        <Typography variant="h3" className={classes.value}>
          <MeasurementInput
            ref={rnRef as Ref<MeasurementInputControl>}
            format="String"
            selectOnFocus
            value={measurment.rnInitials ?? ''}
            onChange={rnInitials => {
              onChange(v => ({ ...v, rnInitials }));
            }}
            onComplete={() => rnRef.current?.blur()}
            onTab={() => {
              keyboardNavigationRef.current = true;
              rnRef.current?.blur();
            }}
            onShiftTab={() => {
              keyboardNavigationRef.current = true;
              painRef.current?.focus();
            }}
            onFocus={handleFocus}
          />
        </Typography>
      </StyledTableCell>
    </StyledTableRow>
  );
};

const useStyles = makeStyles(theme => ({
  centered: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
  },
  root: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
    overflow: 'auto',
  },
  selectedRow: {
    background: theme.palette.secondary.light,
    border: `2px solid ${theme.palette.secondary.main}`,
    '& > *': {
      borderTop: `2px solid ${theme.palette.secondary.main}`,
      borderBottom: `2px solid ${theme.palette.secondary.main}`,
      background: theme.palette.secondary.light,
    },
    '& > *:last-of-type': {
      borderRight: `2px solid ${theme.palette.secondary.main}`,
    },
  },
  value: {
    fontFamily: '"Oswald", sans-serif',
  },
  inlineInput: {
    fontFamily: 'inherit',
    fontSize: '1rem',
    color: theme.palette.text.primary,
    background: 'transparent',
    border: 'none',
    borderBottom: `1px solid ${theme.palette.divider}`,
    outline: 'none',
    minWidth: '3rem',
    padding: '2px 0',
  },
}));

export default ChartV4;
